"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import styles from "./ColorSelectionModal.module.scss";
import ModalManager from "@components/molecules/ModalManager";
import ColorOption from "@components/molecules/ColorOption";
import Button from "@components/atoms/Button";
import assetLoader from "@utils/internalImageLoder";
import { ColorVariant, ColorSelectionData } from "./colorSelectionData";
import ModalShimmer from "@components/molecules/ShortlistCard/ModalShimmer";

interface ColorSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ColorSelectionData;
  onGetQuote?: (selectedColor: ColorVariant) => void;
  hidePricingAndDelivery?: boolean;
  onChangeColour?: (selectedColor: ColorVariant) => void;
  isLoading?: boolean;
}

export default function ColorSelectionModal({
  isOpen,
  onClose,
  data,
  onGetQuote,
  hidePricingAndDelivery = false,
  onChangeColour,
  isLoading = false,
}: ColorSelectionModalProps) {
  const hasColors = data.colors.length > 0;
  const [selectedColor, setSelectedColor] = useState<ColorVariant | null>(
    data.colors.find((color) => color) || data.colors[0] || null
  );

  useEffect(() => {
    if (onChangeColour && selectedColor) {
      onChangeColour(selectedColor);
    }
  }, [onChangeColour, selectedColor]);

  const handleColorSelect = (color: ColorVariant) => {
    setSelectedColor(color);
  };

  const handleGetQuote = () => {
    if (!selectedColor) {
      return;
    }
    onGetQuote?.(selectedColor);
    onClose();
  };
  const modalTitle = `Choose colour for ${data.variantName}`;

  return (
    <ModalManager
      isOpen={isOpen}
      onClose={onClose}
      customClass={styles.modalContent}
      overlayClass={styles.modalOverlay}
      modalHeaderClass={styles.modalHeader}
      title={modalTitle}
      showClose={true}
    >
      <div
        className={`${styles.modalBody} ${
          !data.stockStatus.message ? styles.stockStatusModalBody : ""
        } ${isLoading ? styles.shimmerModalBody : ""}`}
      >
        {isLoading ? (
          <div className={styles.shimmerWrapper}>
            <ModalShimmer />
          </div>
        ) : hasColors ? (
          <div className={styles.colorOptions}>
            {data.colors.map((color) => (
              <ColorOption
                key={color.id}
                color={color}
                isSelected={selectedColor?.id === color.id}
                onClick={handleColorSelect}
                className={styles.colorOption}
                hidePricingAndDelivery={hidePricingAndDelivery}
              />
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateText}>
              {data.emptyStateMessage || "No colors found"}
            </p>
          </div>
        )}

        {/* Stock Status */}
        {!isLoading && (
          <div className={styles.footer}>
            {data.stockStatus.message && (
              <div className={styles.stockStatusContent}>
                <Image
                  src={`${assetLoader()}/icons/time.svg`}
                  alt="Stock warning"
                  width={16}
                  height={16}
                  className={styles.stockIcon}
                />
                <span className={styles.stockMessage}>
                  {data.stockStatus.message}
                </span>
              </div>
            )}

            {/* Confirm Button */}
            {hasColors && (
              <div className={styles.confirmButtonContainer}>
                <Button className={styles.confirmButton} onClick={handleGetQuote}>
                  {data.confirmButtonText}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalManager>
  );
}
